import { logger } from '@core/logger';
import { getPaymentGatewayBrowserBaseWithDevFallback } from './paymentGatewayBrowserBase';
import { getBookingBrowserBase } from './bookingBrowserBase';

export interface PaymentIntentCheckout {
  paymentIntentId: string;
  checkoutUrl?: string;
  status?: string;
  referenceId?: string;
  tenantId?: string;
  amount?: number;
  currency?: string;
  metadata?: Record<string, any>;
}

export interface PaymentIntent {
  id: string;
  status: string;
  amount: number;
  currency: string;
  referenceId: string;
  tenantId?: string;
  metadata: {
    serviceName?: string;
    customerName?: string;
    customerEmail?: string;
    telegramId?: string;
  };
  created: number; // Unix timestamp
  type?: string;
  provider?: string;
  // Stripe-specific fields (nullable for manual capture)
  stripePaymentIntentId?: string | null;
  stripeStatus?: string | null;
  amount_captured?: number | null;
  amount_received?: number | null;
  capture_method?: string | null;
  confirmation_method?: string | null;
  description?: string | null;
  payment_method?: string | null;
  receipt_email?: string | null;
  stripeSessionId?: string | null;
  session?: {
    id: string;
    url: string;
    status: string;
  } | null;
}

export interface Booking {
  id: string;
  service_id: string;
  slot_start_time: string;
  slot_end_time: string;
  customer_name: string;
  customer_email: string;
  telegram_id: string;
  status: string;
  payment_confirmed_at?: string;
  cancelled_at?: string;
  service?: {
    name: string;
    duration: number;
    price: string;
  };
}

const BOOKING_SERVICE_URL = getBookingBrowserBase();

function paymentGatewayFetchBase(): string {
  return getPaymentGatewayBrowserBaseWithDevFallback();
}

export class PaymentService {
  private static async makeRequest<T>(url: string, options?: RequestInit): Promise<T> {
    logger.debug('🔧 Making request to:', url);

    try {
      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...options?.headers,
        },
        ...options,
      });

      if (!response.ok) {
        const errorText = await response.text();
        logger.error('🔧 Response error:', response.status, errorText);
        throw new Error(`HTTP error! status: ${response.status}, body: ${errorText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      logger.error('🔧 Payment service error:', error);
      throw error;
    }
  }

  static async getPaymentIntentCheckout(intentId: string, tenantId?: string): Promise<PaymentIntentCheckout> {
    const qs = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : '';
    const url = `${paymentGatewayFetchBase()}/payment-intents/${intentId}${qs}`;
    return await this.makeRequest<PaymentIntentCheckout>(url);
  }

  /** GET /payment-intents/:id/ui — mode, checkoutUrl, status for /pay/:id (canonical flow). */
  static async getPaymentIntentUi(
    intentId: string,
    tenantId: string,
  ): Promise<{
    id: string;
    tenantId: string;
    status: string;
    channel: string;
    ui: { mode: 'redirect' | 'embedded'; checkoutUrl: string | null; embeddedClientSecret: string | null };
    referenceId?: string;
    amount?: number;
    currency?: string;
  }> {
    const qs = `?tenant_id=${encodeURIComponent(tenantId)}`;
    const url = `${paymentGatewayFetchBase()}/payment-intents/${intentId}/ui${qs}`;
    return await this.makeRequest(url);
  }

  /** Obtener checkout por token firmado (enlace de pago seguro, sin exponer intentId). */
  static async getPaymentIntentCheckoutByToken(token: string): Promise<PaymentIntentCheckout> {
    const encoded = encodeURIComponent(token);
    const url = `${paymentGatewayFetchBase()}/payment-intents/by-token?token=${encoded}`;
    return await this.makeRequest<PaymentIntentCheckout>(url);
  }

  static async getPaymentIntent(intentId: string, tenantId?: string): Promise<PaymentIntent> {
    logger.debug('🔧 Getting payment intent for ID:', intentId);
    const qs = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : '';
    return await this.makeRequest<PaymentIntent>(`${paymentGatewayFetchBase()}/payment-intents/${intentId}/details${qs}`);
  }

  /** Resumen para la UI: status, type, referenceId, detailsUrl, i18nKeys, nextAction (provider-agnostic). */
  static async getPaymentIntentUiSummary(
    intentId: string,
    tenantId?: string,
  ): Promise<{
    status: string;
    type: string;
    referenceId: string;
    detailsUrl?: string;
    channel?: string;
    nextAction?: { type: 'close' | 'redirect' | 'none'; timeoutMs?: number; targetUrl?: string | null };
    i18nKeys: { titleKey: string; descriptionKey: string };
    moduleBasePath?: '/booking' | '/restaurant';
    bookingPreview?: {
      bookingId?: string;
      serviceName?: string;
      slotStart?: string;
      slotEnd?: string;
      customerName?: string;
      customerEmail?: string;
      status?: string;
    };
  }> {
    const qs = tenantId ? `?tenant_id=${encodeURIComponent(tenantId)}` : '';
    return await this.makeRequest(
      `${paymentGatewayFetchBase()}/payment-intents/${intentId}/ui-summary${qs}`,
    );
  }

  static async getBooking(bookingId: string, tenantId?: string): Promise<Booking> {
    logger.debug('📡 Fetching booking:', bookingId);
    const url = tenantId
      ? `${BOOKING_SERVICE_URL}/api/v1/bookings/${bookingId}?tenant_id=${tenantId}`
      : `${BOOKING_SERVICE_URL}/api/v1/bookings/${bookingId}`;
    return await this.makeRequest<Booking>(url);
  }

  /**
   * Cancel as end-customer (no admin JWT). Requires email and/or telegramId matching the booking.
   */
  static async cancelBooking(
    bookingId: string,
    tenantId: string,
    opts?: {
      email?: string | null;
      telegramId?: string | null;
      whatsappId?: string | null;
      customerToken?: string | null;
    },
  ): Promise<void> {
    logger.debug('❌ Cancelling booking (customer):', bookingId);
    const email = (opts?.email || '').trim().toLowerCase();
    const telegramId = (opts?.telegramId || '').trim();
    const whatsappId = (opts?.whatsappId || '').trim();
    if (!email && !telegramId && !whatsappId) {
      throw new Error('email, telegramId or whatsappId required to cancel booking');
    }
    const url = `${BOOKING_SERVICE_URL}/api/v1/bookings/customer/cancel`;
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        // Credencial de identidad (B2-1): sin ella cualquiera podía cancelar la reserva de
        // otra persona conociendo su booking_id y su email.
        ...(opts?.customerToken ? { 'X-Customer-Token': opts.customerToken } : {}),
      },
      body: JSON.stringify({
        tenant_id: tenantId,
        booking_id: bookingId,
        ...(email ? { email } : {}),
        ...(telegramId ? { telegram_id: telegramId } : {}),
        ...(whatsappId ? { whatsapp_id: whatsappId } : {}),
      }),
    });
    if (!res.ok) {
      const errorText = await res.text();
      logger.error('❌ Customer cancel error:', res.status, errorText);
      throw new Error(`HTTP error! status: ${res.status}, body: ${errorText}`);
    }
  }

  /**
   * Cancel a booking using a signed token (e.g. from email link).
   * Calls public booking-actions endpoint; no tenant_id or auth required.
   */
  static async cancelBookingByToken(token: string): Promise<void> {
    const url = `${BOOKING_SERVICE_URL}/api/v1/booking-actions/cancel`;
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: token.trim() }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err?.message || res.statusText || `Error ${res.status}`);
    }
  }

  /**
   * Accept reschedule using a signed token (e.g. from email link).
   */
  static async mintRescheduleTokens(
    tenantId: string,
    bookingId: string,
    email: string,
  ): Promise<{ acceptToken: string; rejectToken: string }> {
    const url = `${BOOKING_SERVICE_URL}/api/v1/booking-actions/mint-reschedule-tokens`;
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        tenant_id: tenantId,
        booking_id: bookingId,
        email: email.trim().toLowerCase(),
      }),
    });
    if (!res.ok) {
      const errorText = await res.text();
      logger.error('mint-reschedule-tokens error:', res.status, errorText);
      throw new Error(`HTTP error! status: ${res.status}, body: ${errorText}`);
    }
    return res.json() as Promise<{ acceptToken: string; rejectToken: string }>;
  }

  static async acceptRescheduleByToken(token: string): Promise<void> {
    const url = `${BOOKING_SERVICE_URL}/api/v1/booking-actions/accept-reschedule`;
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: token.trim() }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err?.message || res.statusText || `Error ${res.status}`);
    }
  }

  /**
   * Reject reschedule using a signed token (e.g. from email link).
   */
  static async rejectRescheduleByToken(token: string): Promise<void> {
    const url = `${BOOKING_SERVICE_URL}/api/v1/booking-actions/reject-reschedule`;
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: token.trim() }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err?.message || res.statusText || `Error ${res.status}`);
    }
  }

  static async redirectToCheckout(checkoutUrl: string): Promise<void> {
    if (!checkoutUrl) {
      throw new Error('Checkout URL is required');
    }
    logger.debug('🔧 Redirecting to checkout URL');
    window.location.href = checkoutUrl;
  }

  /** Get receipt for a payment intent (for client: show download link if receipt exists). */
  static async getReceiptByPaymentIntent(tenantId: string, paymentIntentId: string): Promise<{ id: string; receiptUrl?: string | null } | null> {
    const base = paymentGatewayFetchBase();
    if (!base || !tenantId || !paymentIntentId) return null;
    const url = `${base}/api/v1/receipts/by-payment/${encodeURIComponent(paymentIntentId)}?tenant_id=${encodeURIComponent(tenantId)}`;
    const res = await fetch(url);
    if (!res.ok) return null;
    const data = await res.json();
    return data;
  }

  /** Request a service invoice for a succeeded payment; returns invoice with pdfUrl and id. */
  static async requestServiceInvoice(tenantId: string, paymentIntentId: string): Promise<{ id?: string; pdfUrl?: string | null; pdf_url?: string | null }> {
    const base = paymentGatewayFetchBase();
    if (!base || !tenantId || !paymentIntentId) {
      throw new Error('Missing payment gateway URL, tenant or payment intent');
    }
    const res = await fetch(`${base}/api/v1/invoices/request-service`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tenantId, paymentIntentId }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err?.message || res.statusText || 'Error requesting invoice');
    }
    return res.json();
  }

  /** Build PDF URL using the frontend's payment gateway base (avoids broken stored ngrok URLs). */
  static getInvoicePdfUrl(invoiceId: string, tenantId: string): string | null {
    const base = paymentGatewayFetchBase();
    if (!base || !invoiceId) return null;
    const url = `${base.replace(/\/$/, '')}/api/v1/invoices/${invoiceId}/pdf`;
    return `${url}?tenant_id=${encodeURIComponent(tenantId)}`;
  }
}
